/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  Alert,
  Button,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import { PropsWithChildren, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import '../../assets/common/normalize.css';
import '../../assets/common/reset.css';
import '../../assets/css/designCommon.css';
import '../../assets/css/modelNumberSpecEdit.css';
import UploadImage from '../../assets/image/common/illust_cloud_blue.svg';
import { APIController } from '../../common/API';
import { FileController } from '../../common/FileController';
import DataTransferStep from '../../components/dataTransfer/DataTransferStep';
import {
  DataTransferMaterialOptions,
  DataTransferMaterialTypeEnum,
} from '../../components/dataTransfer/model';
import CalcLoading from '../../components/modelNumberSpecDetails/CalcLoading';
import QuestionPopper from '../../components/popper/QuestionPopper';
import { useAlert } from '../../provider/alert/AlertProvider';
import { useProgress } from '../../provider/progress/ProgressProvider';
import { PutReceivedRequestParam } from '../../rest';
import Modal from '@mui/material/Modal';
import FooterContent from '../../components/footer/Footer';

const uploadDescription = `対応ファイル拡張子は以下の通り。
圧縮ファイル：zip, rar, lzh
書類ファイル：pdf, doc, docx, xls, xlsx, ppt, pptx
画像ファイル：png, jpg, gif, tiff, bmp
3Dファイル：下記（stp, step, stpz, igs, sat, 3dm, dwg, dxf, sldprt, sldasm, x_t, x_b, xmt_txt, prt, stl）
パスワード付ファイルはアップロード不可となります
パスワード付ファイルをやり取りする場合は、チャットでご相談ください
`;
const acceptFileExtension = {
  'application/zip': ['.zip'],
  'application/vnd.rar': ['.rar'],
  'application/x-lzh-compressed': ['.lzh'],

  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],

  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/tiff': ['.tiff', '.tif'],
  'image/bmp': ['.bmp'],

  'application/sla': ['.stl'],
  'model/step': ['.stp', '.step'],
  'application/octet-stream': [
    '.stpz',
    '.igs',
    '.sat',
    '.3dm',
    '.dwg',
    '.dxf',
    '.sldprt',
    '.sldasm',
    '.x_t',
    '.x_b',
    '.xmt_txt',
    '.prt',
  ],
};
const maxFileCount = 10;
const maxFileSize = 1.5; // GB
const tableCellCssStyle = `
  padding: 8px 16px;
`;

interface FileProps {
  name: string;
  size: number;
  uploadId?: string;
}

const DataTransferPage = () => {
  const { setAlertShow, setMessageList } = useAlert();
  const { setProgressShow } = useProgress();
  const navigate = useNavigate();

  const [files, setFiles] = useState<FileProps[]>([]);
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadCount, setUploadCount] = useState(0);

  const [caseNumber, setCaseNumber] = useState('');
  const [material, setMaterial] = useState<DataTransferMaterialTypeEnum | undefined>();
  const [subMaterial, setSubMaterial] = useState<number>();
  const [scheduledDate, setScheduledDate] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [message, setMessage] = useState('');

  const [subMaterialList, setSubMaterialList] = useState<{ value?: number; label?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [progress, setProgress] = useState(0);

  const SnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = parseFloat((bytes / Math.pow(1024, i)).toFixed(2));

    return `${size} ${units[i]}`;
  };

  const fileCount = useMemo(() => files.length, [files]);
  const fileSize = useMemo(() => {
    let size = 0;
    files.forEach((file) => {
      size += file.size;
    });
    return size;
  }, [files]);

  /**
   * Gzip圧縮
   * @param fileData
   * @param fileName
   * @returns
   */
  const getGzipFile = async (fileData: File, fileName: string) => {
    return await new FileController().GetCompressionGzip(fileData, fileName);
  };

  /**
   * アップロードURL取得
   * @param uploadFile
   * @returns
   */
  const getFileUploadURL = async (fileInfoList: { uploadFile: File; fileName: string }[]) => {
    const result = await (
      await APIController.build({
        setAlertShow: setAlertShow,
        navigate: navigate,
      })
    ).GetReceivedFileUploadURL(
      fileInfoList.map((v) => ({ fileName: v.fileName, fileSize: v.uploadFile.size }))
    );
    if (!result) {
      console.error('アップロードURL取得失敗');
      setSnackbarOpen(false);
    }
    return result;
  };

  /**
   * ファイルアップロード
   * @param uploadFile
   */
  const putFileUpload = async (url: string, uploadFile: File) => {
    const result = await new FileController().FileUploadURL(
      url,
      'application/octet-stream',
      uploadFile,
      'gzip',
      setProgress
    );
    if (!result) {
      console.error('ファイルアップロード失敗');
      setAlertShow(true);
      setMessageList([`${uploadFile.name?.replace('.gz', '')} アップロードに失敗しました`]);
      setSnackbarOpen(false);
    }
    return result;
  };

  const onFileDrop = async (newFiles: File[]) => {
    // === ファイルタイプチェック ===
    const supportedExtensions = Object.values(acceptFileExtension).flat();
    
    const invalidFiles = newFiles.filter(file => {
      const fileName = file.name.toLowerCase();
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex === -1) return true;
      
      const fileExtension = fileName.substring(lastDotIndex);
      return !supportedExtensions.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(v => `"${v.name}"`).join('、');
      setAlertShow(true);
      setMessageList([
        invalidFileNames + ' はサポート対象外のファイル形式です',
        '対応ファイル拡張子：' + supportedExtensions.join(', ')
      ]);
      return;
    }
    const newData = [...files];
    newFiles.forEach((v) => {
      if (newData.findIndex((item) => item.name === v.name) === -1) {
        newData.push(v);
      }
    });

    if (newData.length > maxFileCount) {
      setAlertShow(true);
      setMessageList(['ファイル数が上限を超えています']);
      return;
    }
    if (newData.reduce((acc, file) => acc + file.size, 0) > maxFileSize * 1024 * 1024 * 1024) {
      setAlertShow(true);
      setMessageList(['ファイルサイズが上限を超えています']);
      return;
    }

    // 既にアップロード済みのファイルをフィルタリング
    const hasUploadedFiles = newFiles.filter(
      (v) => files.findIndex((item) => item.name === v.name) !== -1
    );
    const hasUploadedFilesMessage =
      hasUploadedFiles.map((v) => `"${v.name}"`).join('、') +
      ' という名前のファイルが既に存在します。';

    if (hasUploadedFiles.length > 0) {
      setAlertShow(true);
      setMessageList([
        hasUploadedFilesMessage,
        'ファイル名を変更するか、既存のファイルを削除してから再度アップロードしてください。',
      ]);
    }

    // アップロード可能なファイルをフィルタリング
    const uploadFiles = newFiles.filter(
      (v) => files.findIndex((item) => item.name === v.name) === -1
    );

    if (uploadFiles.length === 0) return;

    // アップロード中であることを示すダイアログを表示
    setUploadIndex(-1);
    setUploadCount(uploadFiles.length);
    setProgress(0);
    setSnackbarOpen(true);
    setProgressShow(true);

    const closeProgressShow = () => {
      setSnackbarOpen(false);
      setProgressShow(false);
    };

    try {
      // 圧縮
      const gzipList = await Promise.all(
        uploadFiles.map(async (v) => ({
          uploadFile: await getGzipFile(v, v.name),
          fileName: v.name,
        }))
      );

      // アップロードURL取得
      const resultUrls = await getFileUploadURL(gzipList);
      if (resultUrls) {
        resultUrls.forEach(async (v: { [key: string]: any }, i: number) => {
          try {
            setUploadIndex(i);

            const resultUpload = await putFileUpload(v.url, gzipList[i].uploadFile);
            if (resultUpload) {
              const newFile = uploadFiles[i];
              setFiles((prev) => [
                ...prev,
                { name: newFile.name, size: newFile.size, uploadId: v.id },
              ]);

              if (i === uploadFiles.length - 1) {
                closeProgressShow();
              }
            } else {
              closeProgressShow();
            }
          } catch (error) {
            console.error('ファイルアップロード失敗', error);
            closeProgressShow();
          }
        });
      } else {
        closeProgressShow();
      }
    } catch (error) {
      console.error(error);
      closeProgressShow();
    }
  };

  const onFileDropRejected = (fileRejections: FileRejection[]) => {
    const errorFilesMessage = fileRejections
      .map(({ file, errors }) => {
        return errors.map((e) => {
          if (e.code === 'file-invalid-type') {
            return `"${file.name}"`;
          }
          return '';
        });
      })
      .join('、');
    if (errorFilesMessage.length > 0) {
      setAlertShow(true);
      setMessageList([errorFilesMessage, 'サポート対象外のファイルはアップロードできません。']);
    }
  };

  const onFileRemove = (file: FileProps) => {
    setFiles((prev) => prev.filter((v) => v.uploadId !== file.uploadId));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFileDrop,
    onDropRejected: onFileDropRejected,
    maxFiles: 10,
    multiple: true,
    accept: undefined,
  });

  const onMaterialChange = async (material: DataTransferMaterialTypeEnum) => {
    setMaterial(material);
    setSubMaterial(undefined);
    setSubMaterialList([]);

    const result = await (
      await APIController.build({
        setAlertShow: setAlertShow,
        navigate: navigate,
      })
    ).GetDataTransferMaterialList(material);
    if (!result) {
      console.error('マテリアルリスト取得失敗');
      setSnackbarOpen(false);
    } else {
      setSubMaterialList(result);
    }
  };

  const onReset = () => {
    setFiles([]);
    setUploadIndex(0);
    setUploadCount(0);
    setCaseNumber('');
    setMaterial(undefined);
    setSubMaterial(undefined);
    setScheduledDate('');
    setCampaignCode('');
    setMessage('');
    setSubMaterialList([]);
  };

  const onSubmit = async () => {
    if (files.length === 0) {
      setAlertShow(true);
      setMessageList(['ファイルを選択してください']);
      return;
    }
    if (fileSize > maxFileSize * 1024 * 1024 * 1024) {
      setAlertShow(true);
      setMessageList(['ファイルサイズが大きすぎます']);
      return;
    }
    const regex = /^\d{6}$/;
    if (!!caseNumber && !regex.test(caseNumber)) {
      setAlertShow(true);
      setMessageList(['案件番号は6桁の半角数字を入力してください']);
      return;
    }

    // 希望納期のバリデーション
    if (scheduledDate) {
      const selectedDate = new Date(scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 今日の日付のみ比較
      
      if (selectedDate < today) {
        setAlertShow(true);
        setMessageList(['登録日以降の日付を選択してください']);
        return;
      }
    }

    const formData: PutReceivedRequestParam = {
      caseNo: caseNumber,
      dataTransferMaterialId: subMaterial,
      desiredDeliveryDate: scheduledDate,
      campaignCode,
      message,
      receivedFileIds: files.filter((v) => !!v.uploadId).map((v) => v.uploadId!),
    };

    try {
      setLoading(true);
      const result = await (
        await APIController.build({
          setAlertShow: setAlertShow,
          navigate: navigate,
        })
      ).PutReceivedRequest(formData);
      if (!result) {
        console.error('受信要求登録失敗');
        setSnackbarOpen(false);
      } else {
        setShowSuccess(true);
        onReset();
      }
    } catch (error) {
      console.error('受信要求登録失敗');
      setSnackbarOpen(false);
    } finally {
      setLoading(false);
      setSnackbarOpen(false);
    }
  };

  return (
    <div id='MainBody'>
      <div
        css={css`
          width: 100%;
          height: 1px;
          background-color: #eceeef;
          position: fixed;
          top: 56px;
          left: 0;
        `}
      />

      <div className='mainContentsBlock' style={{ marginTop: 56 + 32 }}>
        <div
          className='pageTitleBlock'
          css={css`
            padding-bottom: 32px;
          `}
        >
          <div className='pageTitleInner'>
            <h1 className='pageTitle'>データ転送</h1>
          </div>
        </div>

        <DataTransferStep index={1} name='データ選択' />

        <div
          css={css`
            display: flex;
            gap: 32px;
            align-items: center;
            padding: 16px 0 32px;
          `}
        >
          <div>
            <span style={{ color: 'red' }}>※ </span>
            <span style={{ fontWeight: 'bold' }}>送付データ選択</span>
            <QuestionPopper value={uploadDescription} />
          </div>
          <ul style={{ listStyle: 'none' }}>
            <li>
              ①ファイル数： {fileCount}ファイル / 最大
              <span style={{ color: 'red' }}>{maxFileCount}ファイル</span>
            </li>
            <li>
              ②合計データサイズ： {formatFileSize(fileSize)} / 最大
              <span style={{ color: 'red' }}>{maxFileSize}GB</span>
            </li>
          </ul>
        </div>

        <table
          css={css`
            width: 100%;
            table-layout: fixed;
            margin-bottom: 32px;
          `}
        >
          <thead>
            <tr
              css={css`
                border-top: 1px solid #1c6ecd;
                border-bottom: 1px solid #1c6ecd;
                background-color: #e6eef8;

                th {
                  text-align: left;
                  ${tableCellCssStyle}
                }
              `}
            >
              <th style={{ width: 50 }}></th>
              <th>ファイル名</th>
              <th>ファイルサイズ</th>
              <th style={{ width: 150 }}></th>
            </tr>
          </thead>
          <tbody
            css={css`
              border-bottom: 1px solid #1c6ecd;

              tr:hover {
                opacity: 0.9;
                transition: all 0.3s;
              }

              td {
                border-left: 1px solid #eeeeee;
                ${tableCellCssStyle}
              }
            `}
          >
            {files?.length > 0 ? (
              files.map((item, index) => (
                <tr
                  key={item.uploadId + item.name + item.size}
                  css={css`
                    background-color: ${index % 2 === 0 ? '#e6eef8' : '#fff'};
                    ${tableCellCssStyle}
                  `}
                >
                  <td></td>
                  <td>{item.name}</td>
                  <td>{formatFileSize(item.size)}</td>
                  <td>
                    <Button variant='contained' onClick={() => onFileRemove(item)}>
                      削除
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={3}
                  css={css`
                    text-align: center;
                    font-size: 14px;
                    color: #8b8b8b;

                    ${tableCellCssStyle}
                  `}
                >
                  ファイルをアップロードしてください
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className='modelNoSpecEditFormFileUploadArea' {...getRootProps()}>
          <input {...getInputProps()} />
          <p className='modelNoSpecEditFormFileUploadAreaPara1'>
            <img src={UploadImage} alt='' className='modelNoSpecEditFormFileUploadAreaImage'></img>
            <br />
            ファイルをドラッグ＆ドロップしてください
          </p>
          <p className='modelNoSpecEditFormFileUploadAreaPara2'>
            ファイルを選択する場合は
            <button className='modelNoSpecEditFormFileUploadAreaLink button-style-trans'>
              クリック
            </button>
            してください
          </p>
        </div>

        <DataTransferStep index={2} name='送信情報設定' />

        <div
          css={css`
            padding: 16px 0;
            display: flex;
            flex-direction: column;
          `}
        >
          <FormItem label='案件番号' name='caseNumber'>
            <TextField
              id='caseNumber'
              value={caseNumber}
              onChange={(e) => {
                setCaseNumber(e.target.value);
              }}
              placeholder='当社からご連絡の案件番号がわかる場合はご入力ください'
              style={{ width: 616 }}
            />
          </FormItem>
          <FormItem label='材料' name='material'>
            <div
              css={css`
                display: flex;
                gap: 16px;
                flex: 1;
                align-items: center;
              `}
            >
              <Select
                id='material'
                value={material || ''}
                onChange={(e) => {
                  onMaterialChange(e.target.value as DataTransferMaterialTypeEnum);
                }}
                style={{ width: 200 }}
              >
                <MenuItem value=''>
                  <em>選択してください</em>
                </MenuItem>
                {DataTransferMaterialOptions.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
              {material === DataTransferMaterialTypeEnum.OTHERS ? (
                <div>
                  <span style={{ color: 'red' }}>※ </span>
                  メッセージに希望の材料、個数、後加工などの情報を記載ください
                </div>
              ) : (
                <Select
                  id='subMaterial'
                  value={subMaterial || ''}
                  onChange={(e) => {
                    setSubMaterial(e.target.value as number);
                  }}
                  style={{ width: 400 }}
                >
                  <MenuItem value=''>
                    <em>選択してください</em>
                  </MenuItem>
                  {subMaterialList?.map((v) => (
                    <MenuItem key={v.value} value={v.value}>
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </div>
          </FormItem>
          <FormItem label='希望納期' name='scheduledDate'>
            <TextField
              id='scheduledDate'
              type='date'
              value={scheduledDate}
              onChange={(e) => {
                setScheduledDate(e.target.value);
              }}
              placeholder='希望納期を入力してください'
              style={{ width: 200 }}
              InputProps={{
                inputProps: {
                  min: new Date(Date.now()).toISOString().split('T')[0]
                }
              }}
            />
          </FormItem>
          <FormItem label='プロモーションコード' name='campaignCode'>
            <TextField
              id='campaignCode'
              value={campaignCode}
              onChange={(e) => {
                setCampaignCode(e.target.value);
              }}
              style={{ width: 616 }}
            />
          </FormItem>
          <FormItem label='メッセージ' name='message'>
            <TextField
              id='message'
              multiline
              rows={4}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              placeholder='個数、造形方式、後加工、その他希望ございましたらこちらにご入力ください'
              style={{ width: '100%' }}
              inputProps={{ maxLength: 5000 }}
              helperText={`${message.length}/5000`}
              FormHelperTextProps={{ style: { textAlign: 'right' } }}
            />
          </FormItem>
        </div>
        <Button
          variant='contained'
          size='large'
          onClick={onSubmit}
          css={css`
            padding: 8px 40px;
            margin: auto;
            display: block;
          `}
        >
          登録
        </Button>
      </div>

      <div className='fixedListControlBlock' style={{ height: 56 }}>
        <FooterContent />
      </div>

      <CalcLoading show={loading} />

      <Snackbar
        open={snackbarOpen}
        onClose={SnackbarClose}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 20000 }}
      >
        <Alert severity='info' onClose={SnackbarClose} sx={{ backgroundColor: '#1c6ecd' }}>
          <div>
            {uploadIndex === -1 ? (
              'アップロード準備中'
            ) : (
              <>
                {progress}% {uploadIndex + 1}/{uploadCount}
                <LinearProgress variant='determinate' value={progress} color='inherit' />
              </>
            )}
          </div>
        </Alert>
      </Snackbar>

      <Modal
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        open={showSuccess}
      >
        <div className='modalWindowBlock modalRemindMessage' style={{ minWidth: '500px' }}>
          <div className='modalWindowInner modalWindowNoHeader'>
            <div className='modalWindowContents'>
              <h1 className='modalRemindMessageTitle'>成功</h1>
              <p className='modalRemindMessagePara'>
                データ登録いただきありがとうございます。
                <br />
                見積もりに際して担当者からご連絡させていただきます。
              </p>
              <div className='modalRemindMessageBtnBlock'>
                <button
                  className='btnWhiteBaseGray'
                  type='button'
                  name='button'
                  onClick={() => {
                    setShowSuccess(false);
                    navigate('/ModelNumberList');
                  }}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const FormItem = ({
  label,
  name,
  children,
}: PropsWithChildren<{ label: string; name: string }>) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 32px;
        padding-bottom: 16px;
      `}
    >
      <label
        htmlFor={name}
        css={css`
          width: 120px;
          text-align: right;
        `}
      >
        {label}
      </label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

export default DataTransferPage;
